import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, Trash2, X, Filter } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useDevDebug } from "@/contexts";

export const DevDebugPanel = () => {
  const {
    logs,
    errors,
    isDebugPanelOpen,
    closeDebugPanel,
    clearLogs,
    clearErrors,
  } = useDevDebug();

  const [copied, setCopied] = useState(false);
  const [logFilter, setLogFilter] = useState<'all' | 'log' | 'warn' | 'error' | 'info'>('all');

  if (!import.meta.env.DEV) return null;

  const handleCopyAll = async () => {
    const debugInfo = `
=== DEV DEBUG PANEL EXPORT ===
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}

=== ERRORS (${errors.length}) ===
${errors.map((err, i) => `
[${i + 1}] ${err.timestamp}
Message: ${err.message}
${err.stack ? `Stack: ${err.stack}` : ''}
${err.componentStack ? `Component Stack: ${err.componentStack}` : ''}
${err.additionalInfo ? `Additional Info: ${JSON.stringify(err.additionalInfo, null, 2)}` : ''}
`).join('\n---\n')}

=== CONSOLE LOGS (${logs.length}) ===
${logs.map(log => 
  `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}] ${log.message}${log.data ? `\nData: ${JSON.stringify(log.data)}` : ''}`
).join('\n')}
    `.trim();

    try {
      await navigator.clipboard.writeText(debugInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredLogs = logFilter === 'all' 
    ? logs 
    : logs.filter(log => log.level === logFilter);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'warn': return 'text-yellow-600 dark:text-yellow-400';
      case 'info': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-muted-foreground';
    }
  };

  const getLevelBadgeVariant = (level: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (level) {
      case 'error': return 'destructive';
      case 'warn': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <Dialog open={isDebugPanelOpen} onOpenChange={closeDebugPanel}>
      <DialogContent className="max-w-5xl max-h-[90vh] w-[90vw]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg flex items-center gap-2">
                🐛 Dev Debug Panel
                <Badge variant="outline" className="font-mono text-xs">
                  Ctrl+Shift+D
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs mt-1">
                Site-wide debugging console • {logs.length} logs • {errors.length} errors
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCopyAll}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Copy All
                  </>
                )}
              </Button>
              <Button onClick={closeDebugPanel} variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="logs" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="logs" className="gap-2">
              Console Logs
              <Badge variant="secondary" className="text-xs">
                {logs.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="errors" className="gap-2">
              Errors
              {errors.length > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {errors.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="logs" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <div className="flex gap-1">
                  {(['all', 'log', 'info', 'warn', 'error'] as const).map(level => (
                    <Button
                      key={level}
                      variant={logFilter === level ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setLogFilter(level)}
                      className="h-7 text-xs"
                    >
                      {level === 'all' ? 'All' : level.charAt(0).toUpperCase() + level.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              <Button
                onClick={clearLogs}
                variant="ghost"
                size="sm"
                className="gap-2 h-7 text-xs"
              >
                <Trash2 className="h-3 w-3" />
                Clear
              </Button>
            </div>

            <ScrollArea className="h-[50vh] w-full rounded-md border">
              <div className="p-4 space-y-2 font-mono text-xs">
                {filteredLogs.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No logs to display
                  </div>
                ) : (
                  filteredLogs.map((log, i) => (
                    <div
                      key={i}
                      className={`p-2 rounded border-l-2 ${
                        log.level === 'error' ? 'border-l-red-500 bg-red-50 dark:bg-red-950/20' :
                        log.level === 'warn' ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/20' :
                        log.level === 'info' ? 'border-l-blue-500 bg-blue-50 dark:bg-blue-950/20' :
                        'border-l-gray-300 bg-muted/50'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-1">
                        <Badge variant={getLevelBadgeVariant(log.level)} className="text-[10px] px-1 py-0">
                          {log.level.toUpperCase()}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {log.timestamp.toLocaleTimeString()}.{log.timestamp.getMilliseconds()}
                        </span>
                      </div>
                      <div className={getLevelColor(log.level)}>
                        {log.message}
                      </div>
                      {log.data && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                            View data
                          </summary>
                          <pre className="mt-1 p-2 bg-gray-900 text-green-400 rounded text-[10px] overflow-x-auto">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="errors" className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={clearErrors}
                variant="ghost"
                size="sm"
                className="gap-2 h-7 text-xs"
              >
                <Trash2 className="h-3 w-3" />
                Clear
              </Button>
            </div>

            <ScrollArea className="h-[50vh] w-full rounded-md border">
              <div className="p-4 space-y-4">
                {errors.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No errors to display 🎉
                  </div>
                ) : (
                  errors.map((error, i) => (
                    <div
                      key={i}
                      className="p-3 rounded border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/20 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-red-600 dark:text-red-400">
                          Error #{i + 1}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {error.timestamp}
                        </div>
                      </div>
                      
                      <div className="text-sm font-medium text-red-700 dark:text-red-300">
                        {error.message}
                      </div>

                      {error.url && (
                        <div className="text-xs text-muted-foreground">
                          URL: {error.url}
                        </div>
                      )}

                      {error.stack && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                            Stack trace
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 text-red-400 rounded text-[10px] overflow-x-auto font-mono">
                            {error.stack}
                          </pre>
                        </details>
                      )}

                      {error.componentStack && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                            Component stack
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 text-orange-400 rounded text-[10px] overflow-x-auto font-mono">
                            {error.componentStack}
                          </pre>
                        </details>
                      )}

                      {error.additionalInfo && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                            Additional info
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 text-blue-400 rounded text-[10px] overflow-x-auto font-mono">
                            {JSON.stringify(error.additionalInfo, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
